import {
  Box,
  Typography,
  useTheme,
  Autocomplete,
  debounce,
  TextField,
} from "@mui/material";
import { FC, useEffect, useState, useCallback } from "react";
import ModalBase from "../../../../components/ModalBase";
import StatusChecker from "../../../../components/StatusChecker";
import CustomButton from "../../../../components/CustomButton";
import { WebsiteManager } from "../../../../services/api/WebsiteManager";
import CustomTextField from "../../../../components/CustomTextField";
import CloseIcon from "@mui/icons-material/Close";
import { ExternalApiManager } from "../../../../services/api/ExternalApiManager";
import { showFlashMessageWithTimeout } from "../../../../services/actions/flashMessage-actions";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useStyles } from "../AddApiKeyModal/addApiKeyModal.style";
import { ApiKeyModalProps } from "../AddApiKeyModal/types";

// Interface for place predictions from Google API
interface PlacePrediction {
  description: string;
  placeId: string;
}

export const GoogleReviewApiKeyModal: FC<ApiKeyModalProps> = ({
  isOpen,
  onClose,
  createdWebId,
  websiteData,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // State declarations
  const [placeId, setPlaceId] = useState<string>(
    websiteData?.googleReviewsPlaceId ?? "",
  );
  const [businessName, setBusinessName] = useState<string>(
    websiteData?.googleReviewsBusinessName ?? "",
  );
  const [placesQuery, setPlacesQuery] = useState<string>(
    websiteData?.googleReviewsPlacesQuery ?? "",
  );
  const [placePredictions, setPlacePredictions] = useState<PlacePrediction[]>(
    [],
  );

  // Loading and verification states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCheckLoading, setIsCheckLoading] = useState<boolean>(false);
  const [loadingPredictions, setLoadingPredictions] = useState<boolean>(false);
  const [isClearingCache, setIsClearingCache] = useState<boolean>(false);
  const [isVerified, setIsVerified] = useState<boolean>(
    Boolean(websiteData?.googleReviewsPlaceId),
  );

  // Reset form values when modal is opened or website data changes
  useEffect(() => {
    if (isOpen) {
      setPlacesQuery(websiteData?.googleReviewsPlacesQuery ?? "");
      setPlaceId(websiteData?.googleReviewsPlaceId ?? "");
      setBusinessName(websiteData?.googleReviewsBusinessName ?? "");
      setIsVerified(Boolean(websiteData?.googleReviewsPlaceId));
    }
  }, [
    isOpen,
    websiteData?.googleReviewsPlacesQuery,
    websiteData?.googleReviewsPlaceId,
    websiteData?.googleReviewsBusinessName,
  ]);

  // Fetch places from Google Maps API
  const fetchPlaces = useCallback(
    async (query: string) => {
      if (!query || query.length <= 2) {
        setPlacePredictions([]);
        return;
      }

      try {
        setLoadingPredictions(true);
        const externalApiManager = ExternalApiManager.getManager();
        const result = await externalApiManager.getGooglePlacesPredictions(
          query,
        );

        if (result?.predictions) {
          const predictions = result.predictions.map(
            (prediction: { description: string; place_id: string }) => ({
              description: prediction.description,
              placeId: prediction.place_id,
            }),
          );
          setPlacePredictions(predictions);
        }
      } catch (error) {
        console.error("Error fetching places:", error);
        showFlashMessageWithTimeout(
          dispatch,
          t("errors.placesApiError"),
          "error",
        );
      } finally {
        setLoadingPredictions(false);
      }
    },
    [dispatch, t],
  );

  // Debounced version of fetchPlaces
  const debouncedFetchPlaces = debounce(
    (query: string) => fetchPlaces(query),
    300,
  );

  // Handle places query change
  const handlePlacesQueryChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setPlacesQuery(value);

    if (value && value.length > 2) {
      debouncedFetchPlaces(value);
    }
  };

  // Handle place selection
  const handlePlaceSelection = (
    _event: React.SyntheticEvent,
    value: PlacePrediction | null,
  ) => {
    if (value) {
      setPlaceId(value.placeId);
      // If business name is empty, use the place description
      if (!businessName) {
        setBusinessName(value.description.split(",")[0]);
      }
    } else {
      setPlaceId("");
    }
  };

  // Handle business name change
  const handleBusinessNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setBusinessName(event.target.value);
  };

  // Verify the Google Place ID
  const handleVerification = async () => {
    if (!placeId) {
      showFlashMessageWithTimeout(
        dispatch,
        t("popups.fillApiAndPlaceId"),
        "info",
      );
      return;
    }

    try {
      setIsCheckLoading(true);
      const externalApiManager = ExternalApiManager.getManager();
      const result = await externalApiManager.verifyGoogleReviewsApi(placeId);

      setIsVerified(result.isVerified);
      showFlashMessageWithTimeout(
        dispatch,
        result.isVerified
          ? t("popups.googleReviewsWasVerified")
          : t("popups.verificationFailed"),
        result.isVerified ? "success" : "error",
      );
    } catch (error: any) {
      setIsVerified(false);
      showFlashMessageWithTimeout(dispatch, error.toString(), "error");
    } finally {
      setIsCheckLoading(false);
    }
  };

  // Save Google Reviews data
  const handleSave = async () => {
    if (!createdWebId || !businessName || !placeId || !isVerified) {
      showFlashMessageWithTimeout(dispatch, t("popups.fillAllFields"), "info");
      return;
    }

    try {
      setIsLoading(true);
      const websiteManager = WebsiteManager.getManager();
      const result = await websiteManager.updateGoogleReviewsAsync(
        createdWebId,
        placesQuery,
        placeId,
        businessName,
      );

      if (result) {
        showFlashMessageWithTimeout(dispatch, t("popups.added"), "success");
        onClose();
      }
    } catch (error: any) {
      showFlashMessageWithTimeout(dispatch, error.toString(), "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Default place for Autocomplete
  const defaultPlace =
    placeId && placesQuery ? { description: placesQuery, placeId } : null;

  return (
    <ModalBase isOpen={isOpen} onClose={onClose} center>
      <Box className={classes.body}>
        <Box className={classes.iconContainer}>
          <CloseIcon
            style={{ color: theme.palette.natural.t2, cursor: "pointer" }}
            onClick={onClose}
          />
        </Box>

        <Typography className={classes.title}>
          {t(
            "campaign.notifications.settings.displayRules.googleReviews.title",
          )}
        </Typography>

        <Box style={{ marginLeft: 32, marginRight: 32, marginBottom: 16 }}>
          <Typography mb={2}>
            {t(
              "campaign.notifications.settings.displayRules.googleReviews.description",
            )}
          </Typography>
        </Box>

        <Box className={classes.inputsContainer} sx={{ marginBottom: 2 }}>
          <CustomTextField
            style={{ width: "100%" }}
            fullWidth
            label={String(
              t(
                "campaign.notifications.settings.displayRules.googleReviews.companyName",
              ),
            )}
            isRequired
            value={businessName}
            onChange={handleBusinessNameChange}
          />
        </Box>

        {/* Place Search Autocomplete */}
        <Box className={classes.inputsContainer}>
          <Autocomplete
            fullWidth
            value={defaultPlace}
            options={placePredictions}
            getOptionLabel={option => option.description}
            loading={loadingPredictions}
            noOptionsText={t(
              "campaign.notifications.settings.displayRules.googleReviews.noOptions",
            )}
            onChange={handlePlaceSelection}
            inputValue={placesQuery}
            onInputChange={(_event, newValue) => setPlacesQuery(newValue)}
            renderInput={params => (
              <TextField
                {...params}
                fullWidth
                label={t(
                  "campaign.notifications.settings.displayRules.googleReviews.searchPlace",
                )}
                onChange={handlePlacesQueryChange}
              />
            )}
          />
        </Box>

        <Box style={{ marginLeft: 32, marginRight: 32 }}>
          {/* Place ID Display */}
          {placeId && (
            <CustomTextField
              style={{ marginRight: 24, marginTop: 10 }}
              fullWidth
              label={String(
                t(
                  "campaign.notifications.settings.displayRules.googleReviews.placeId",
                ),
              )}
              value={placeId}
              disabled
            />
          )}

          {/* Instructions */}
          <Typography className={classes.listTitle} sx={{ marginTop: 4 }}>
            {t(
              "campaign.notifications.settings.displayRules.googleReviews.howToAddApiKey.title",
            )}
          </Typography>

          <Box className={classes.rowWithMargin}>
            <Typography className={classes.listItemNumber}>1.</Typography>
            <Typography className={classes.listItemText}>
              {t(
                "campaign.notifications.settings.displayRules.googleReviews.howToAddApiKey.first",
              )}
            </Typography>
          </Box>

          <Box className={classes.rowWithMargin}>
            <Typography className={classes.listItemNumber}>2.</Typography>
            <Typography className={classes.listItemText}>
              {t(
                "campaign.notifications.settings.displayRules.googleReviews.howToAddApiKey.second",
              )}
            </Typography>
          </Box>

          <Box style={{ display: "flex", flexDirection: "row" }}>
            <Typography className={classes.listItemNumber}>3.</Typography>
            <Typography className={classes.listItemText}>
              {t(
                "campaign.notifications.settings.displayRules.googleReviews.howToAddApiKey.third",
              )}
            </Typography>
          </Box>

          {/* Status Checker */}
          <StatusChecker
            isActive={isVerified}
            isLoading={isCheckLoading}
            onClick={handleVerification}
          />

          {/* Action Buttons */}
          <Box className={classes.buttonContainer}>
            <CustomButton
              isLoading={isLoading}
              title={isVerified ? t("saveChanges") : t("add")}
              onClick={handleSave}
              isDisabled={!isVerified}
              style={{ width: 120, marginLeft: 20 }}
            />
            <CustomButton
              isLoading={false}
              title={t("close")}
              type='secondary'
              onClick={onClose}
              style={{ width: 120 }}
            />
          </Box>
        </Box>
      </Box>
    </ModalBase>
  );
};
